import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { Case } from '../../../../../../../../_base-shared/models/Case/Case';
import { CaseCreditor } from '../../../../../../../../_base-shared/models/Case/CaseCreditor';
import {
  CaseDepartmentAssignment
} from '../../../../../../../../_base-shared/models/Department/CaseDepartmentAssignment';
import { DepartmentCategory } from '../../../../../../../../_base-shared/models/Department/DepartmentCategory';
import { PaymentTerm } from '../../../../../../../../_base-shared/models/Payment/PaymentTerm';
import { Product } from '../../../../../../../../_base-shared/models/Product';
import { CaseCreditorStatus } from '../../../../../../../../_base-shared/models/Status/CaseCreditorStatus';
import { User } from '../../../../../../../../_base-shared/models/User/User';
import { environment } from '../../../../../../environments/environment';
import { MainGlobalEventService } from '../../../../../_shared/services/main-global-event.service';
import { MiscConfigService } from '../../../../config/misc-config/misc-config.service';
import { CreditorService } from '../../../../creditor/creditor.service';
import { DepartmentService } from '../../../../department/department.service';
import { CaseCreditorStatusService } from '../../../../status/case-creditor/status-list/case-creditor-status.service';
import { CaseCreditorService } from '../../../case-creditor.service';
import { ProductService } from '../../../product.service';
import { CalculationModalComponent } from '../../case-creditor-list/calculation-modal/calculation-modal.component';

@Component({
  standalone: false,
  selector:    'app-case-creditor-status-editor',
  templateUrl: './case-creditor-status-editor.component.html',
  styleUrls:   ['./case-creditor-status-editor.component.scss']
})
export class CaseCreditorStatusEditorComponent implements OnInit {
  @Input() case: Case;
  @Input() caseCreditor: CaseCreditor;
  @Input() settlementBalance: number;
  @Output() caseDepartmentsUpdated: EventEmitter<Array<CaseDepartmentAssignment>> = new EventEmitter<Array<CaseDepartmentAssignment>>();

  public form: UntypedFormGroup;
  public formActive: boolean;
  public isEditing                                       = false;
  public isLoading                                       = 0;
  public isSubmitting: boolean;
  public departmentCategories: Array<DepartmentCategory>;
  public caseDepartmentAssignments: Array<CaseDepartmentAssignment>;
  public authUser: User;
  public filteredDepartmentCategories: Array<DepartmentCategory>;
  public allowedUsers                                    = [747150, 751524, 751600, 27, 752389, 752733, 988713];
  public canEditCaseAssignments                          = [27, 843623, 764677, 34, 1, 988713];
  public caseCreditorStatuses: Array<CaseCreditorStatus> = [];
  public defaultNegotiationPercent: number;
  public defaultTaxRate: number;
  public defaultFdsPercentage: number;
  public creditorFee: number;
  public negotiationFee: number;
  public debtRequestFee: number;
  public products: Array<Product>                        = [];

  constructor(private fb: UntypedFormBuilder,
              private toastr: ToastrService,
              private globalEventsService: MainGlobalEventService,
              private departmentService: DepartmentService,
              private dialog: MatDialog,
              private caseCreditorStatusService: CaseCreditorStatusService,
              private configService: MiscConfigService,
              private translate: TranslateService,
              private creditorService: CreditorService,
              private productService: ProductService,
              private caseCreditorService: CaseCreditorService) {
  }

  ngOnInit(): void {
    this.fetchCategorizedDepartments();
    this.fetchCaseCreditorStatuses();
    this.fetchProducts();
  }

  private buildForm(caseId: number, caseDepartmentAssignments: Array<CaseDepartmentAssignment>): void {
    this.formActive = false;

    this.form = this.fb.group({
      case_creditor_status_id: [{value: this.caseCreditor.case_creditor_status.id, disabled: true}],
      product_id:              [null],
    });

    this.globalEventsService.authUser$.subscribe(user => {
      this.authUser                     = user;
      this.filteredDepartmentCategories = this.departmentCategories;

      this.filteredDepartmentCategories.forEach(departmentCategory => {
        departmentCategory.departments.forEach(department => {
          const assignedUserId = caseDepartmentAssignments.filter(caseDepartmentAssignment => {
            return caseDepartmentAssignment.case_id === caseId &&
              caseDepartmentAssignment.department_id === department.id;
          }).map(caseAssignment => caseAssignment.user_id)[0];

          this.form.addControl(
            department.type,
            this.fb.control({value: assignedUserId, disabled: true}),
          );
        });
      });

      setTimeout(() => this.formActive = true, 0);

      this.form.get('case_creditor_status_id').valueChanges.subscribe(value => {
        const matchingStatus = this.caseCreditorStatuses.find(status => status.id === value).name_en;

        if (matchingStatus.includes('dm')) {
          const product = this.products.find(p => p.name.toLowerCase() === 'dmp');
          this.form.get('product_id').setValue(product.id);
        }
        if (matchingStatus.includes('fds')) {
          const product = this.products.find(p => p.name.toLowerCase() === 'fds');
          this.form.get('product_id').setValue(product.id);
        }
        if (matchingStatus.includes('qw') || matchingStatus.includes('refinanced')
          || matchingStatus.includes('exclude') || matchingStatus.includes('declined')) {
          const product = this.products.find(p => p.name.toLowerCase() === 'exclude');
          this.form.get('product_id').setValue(product.id);
        }
        if (matchingStatus.includes('ds')) {
          const product = this.products.find(p => p.name.toLowerCase() === 'debt settlement');
          this.form.get('product_id').setValue(product.id);
        }
      });
    });

    if (this.case.original_packager_id !== this.case.packager_id) {
      this.form.get('legal-advisor')?.disable({onlySelf: true});
    }
  }


  public isElementRestricted() {
    const enabledRoles         = environment.ALLOW_CASES_ROLES_IDS;
    const enabledStatusId      = environment.BLOCK_CASES_STATUS_IDS;
    const enabledDepartmentsId = environment.ALLOW_CASES_DEPARTMENTS_IDS;

    const isDepartmentAllowed = this.authUser.departments.some(department =>
      enabledDepartmentsId.includes(department.id)
    );

    if (
      ! enabledStatusId.includes(this.case.status_id) ||
      enabledRoles.includes(this.authUser.role_id) ||
      isDepartmentAllowed
    ) {
      return true;
    }
    return false;
  }

  public toggleEditMode(state: boolean): void {

    const enabledRoles         = environment.ALLOW_CASES_ROLES_IDS;
    const enabledStatusId      = environment.BLOCK_CASES_STATUS_IDS;
    const enabledDepartmentsId = environment.ALLOW_CASES_DEPARTMENTS_IDS;

    const isDepartmentAllowed = this.authUser.departments.some(department =>
      enabledDepartmentsId.includes(department.id)
    );

    this.isEditing = state;
    if (this.isEditing) {
      this.form.get('case_creditor_status_id').enable();
      /*if (!this.canEditCaseAssignments.includes(this.authUser.id)) {
        const formControl = this.form.get('creditor-negotiator');
        formControl?.disable();
      }*/
    } else {
      this.form.get('case_creditor_status_id').disable();
    }
    /*  if (this.case.original_packager_id !== this.case.packager_id) {
        this.form.get('legal-advisor').disable({onlySelf: true});
      }*/
  }

  public submitForm(form: UntypedFormGroup): void {
    if (form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading++;
    this.caseCreditorService.updateCaseCreditor(this.case.id, this.caseCreditor.id, this.form.getRawValue())
      .pipe(finalize(() => this.isLoading--))
      .subscribe(
        value => {
          this.toastr.success(this.translate.instant('CASES.details.change-status-success'));
        },
        error => {
          if (error.error?.data?.message) {
            this.toastr.error(error.error.data.message);
          } else {
            this.toastr.error(this.translate.instant('CASES.details.change-status-error'));
          }
        });
  }

  private fetchCategorizedDepartments(): void {
    this.isLoading++;
    this.departmentService.categoryIndex(['departments.users'])
      .pipe(finalize(() => this.isLoading--)).subscribe(result => {
      this.departmentCategories = result.data.filter(category =>
        category.type === 'creditor-negotiation'
      );
      this.fetchCaseDepartmentAssignments(this.case.id);
    });
  }

  private fetchCaseDepartmentAssignments(caseId: number): void {
    this.isLoading++;
    this.departmentService.getCaseDepartmentAssignments(caseId).pipe(finalize(() => this.isLoading--))
      .subscribe(result => {
        this.caseDepartmentAssignments = result.data;
        this.buildForm(caseId, this.caseDepartmentAssignments);
      });
  }

  private fetchCaseCreditorStatuses(): void {
    this.isLoading++;
    this.caseCreditorStatusService.index({select_all: 1}).pipe(finalize(() => this.isLoading--))
      .subscribe(res => this.caseCreditorStatuses = res.data);
  }

  public openCalculationModal($event: Event, caseCreditor: CaseCreditor): void {
    $event.stopPropagation();
    this.dialog.open(CalculationModalComponent, {
      width: 'fit-content',
      data:  {
        caseCreditor,
        settlementBalance: this.settlementBalance,
      }
    });
  }

  public requestDebtSettlement($event: Event, caseCreditor: CaseCreditor | any, clientCase: Case): void {
    $event.stopPropagation();
    // if the settlement_balance is not sufficient, show error

    this.configService.getConfigData().subscribe(next => {
      this.defaultNegotiationPercent = next.data.find(obj => obj.key === 'ds_reduction_percent').value;
      this.defaultTaxRate            = next.data.find(obj => obj.key === 'default_iva_rate').value;
      this.defaultFdsPercentage      = next.data.find(obj => obj.key === 'ds_default_fds_reduction').value;
      // cast this.defaultFdsPercentage to number
      this.defaultFdsPercentage      = +this.defaultFdsPercentage;

      // calculate reduction percentage
      const reductionPercentage = ((caseCreditor.initial_balance - caseCreditor.negotiated_amount) / caseCreditor.initial_balance) * 100;

      console.log((this.case.settlement_balance > caseCreditor.negotiated_amount));
      console.log((reductionPercentage > this.defaultFdsPercentage));
      this.creditorFee    = caseCreditor.negotiated_amount;
      this.negotiationFee = ((caseCreditor.initial_balance - caseCreditor.negotiated_amount) * this.defaultNegotiationPercent / 100);
      this.debtRequestFee = +this.creditorFee + +this.negotiationFee;

      const html     = `
          <table style="text-align: left;">
              <tr>
                  <td>${this.translate.instant('CREDITORS.debt-settlement-request.negotiation-fee')}: ${this.negotiationFee}</td>
              </tr>
              <tr>
                  <td>${this.translate.instant('CREDITORS.debt-settlement-request.total-quick-win')}: ${this.debtRequestFee}</td>
              </tr>
          </table>
        `;
      let buttonText = this.translate.instant('CREDITORS.debt-settlement-request.actions.send');
      if (caseCreditor.case_creditor_status.slug === 'ds_settlement_requested') {
        buttonText = this.translate.instant('CREDITORS.debt-settlement-request.actions.accept');
      }

      Swal.fire({
        title:              this.translate.instant('CREDITORS.debt-settlement-request.title') + ': ' + caseCreditor.creditor.name,
        html,
        showCancelButton:   true,
        confirmButtonText:  buttonText,
        confirmButtonColor: '#886ab5',
        cancelButtonText:   this.translate.instant('SHARED.cancel'),
      }).then(res => {
        if (res.isConfirmed) {
          this.isLoading++;
          const data                   = {};
          const status                 = this.caseCreditorStatuses.find(status => status.slug === 'ds_settlement_requested');
          // @ts-ignore
          data.case_creditor_status_id = status.id;
          // @ts-ignore
          data.product_id              = 19;

          if (caseCreditor.case_creditor_status.slug === 'ds_settlement_requested') {
            // @ts-ignore
            data.case_creditor_status_id = status.id;
          }

          this.caseCreditorService.updateCaseCreditor(this.case.id, caseCreditor.id, data)
            .pipe(finalize(() => this.isLoading--))
            .subscribe(
              value => {
                this.toastr.success(this.translate.instant('CASES.details.change-status-success'));
                Swal.close();
              },
              error => {
                if (error.error?.data?.message) {
                  this.toastr.error(error.error.data.message);
                } else {
                  this.toastr.error(this.translate.instant('CASES.details.change-status-error'));
                }
              });
        }
      });
    });
  }

  private fetchProducts() {
    this.isLoading++;
    this.productService.index({select_all: 1}).pipe(finalize(() => this.isLoading--)).subscribe(
      result => this.products = result.data
    );
  }
}
